import { Database, Loader2 } from 'lucide-react';
import { IS_DEMO } from '../../apiBase';

// IN THE PARTS DB — the mark AND the switch, in one control.
//
// It is the same answer to the same question wherever it appears: is this picture (or this
// mesh) the part's, in the database every other phase reads from — and pressing it is how
// that becomes true. It was written out three times, on the generated-image tiles, in the
// corner of the mesh viewer and now in the zoom, which is three places for one appearance
// to drift: a grey square that means "not in" here and a missing badge that means the same
// thing there is the kind of difference nobody notices until they are counting.
//
// DRAWN IN BOTH STATES, which is the point of a switch rather than a badge. Emerald and
// filled means the database has it; grey means it does not and clicking puts it there. A
// badge that only appeared when the answer was yes left "no" looking like "not applicable",
// and the control for it somewhere else entirely.
export function DbSwitch({
  on, busy, disabled, title, size = 'sm', onWhite = false, className = '', onToggle,
}: {
  on: boolean;
  busy?: boolean;
  disabled?: boolean;
  // What it means HERE, in this caller's own words — a mesh is "the part's 3D model", a
  // render is "one of its photographs". Passed in rather than chosen from the state, so the
  // switch never has to know which of the two it is switching.
  title: string;
  size?: 'sm' | 'md';
  // The switch sits on the mesh viewer's viewport, which is white in BOTH themes on purpose
  // (see MeshViewer). Its dark-theme colours would be a dark button on white there, so this
  // drops them. Everywhere else the surface follows the theme and they are needed.
  onWhite?: boolean;
  className?: string;
  onToggle: () => void;
}) {
  const icon = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
  // NOTHING TO SWITCH IN THE DEMO, which keeps nothing. The mark still says what the real app
  // keeps; pressing it does nothing — no request, no refusal read back, no hover inviting one.
  const inert = IS_DEMO;
  return (
    <button
      type="button"
      onClick={inert ? undefined : onToggle}
      disabled={disabled || busy}
      role="switch"
      aria-checked={on}
      title={title}
      aria-label={title}
      className={`rounded-md border shadow-sm backdrop-blur transition-colors disabled:opacity-50 ${
        size === 'sm' ? 'p-1' : 'p-1.5'
      } ${
        on
          ? `border-blue-600 bg-blue-600 text-white${inert ? '' : ' hover:bg-blue-500'}`
          : `border-slate-300 bg-white/90 text-slate-400${
            inert ? '' : ' hover:border-blue-500 hover:text-blue-600'
          }${onWhite ? '' : ' '}`
      } ${inert ? 'cursor-default' : ''} ${className}`}
    >
      {busy ? <Loader2 className={`${icon} animate-spin`} /> : <Database className={icon} />}
    </button>
  );
}
